import React from "react";
import DrawerHeader from "@/components/DrawerHeader";
import FullScreenDrawer from "@/components/FullScreenDrawer";
import theme from "@/theme";
import { DroppsProduct } from "@/store";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { CancelSubscriptionPage } from "./pages/CancelSubscriptionPage";
import { ConfirmItemPage } from "./pages/ConfirmItemPage";
import { DelayShipmentPage } from "./pages/DelayItemPage";
import { EditProductPage } from "./pages/EditProductPage";
import { EditSubscriptionPage } from "./pages/EditItemPage";
import { ProductSelectorPage } from "./pages/ProductSelectorPage";
import { UpdateAddressPage } from "./pages/UpdateAddressPage";
import { UpdateIntervalPage } from "./pages/UpdateIntervalPage";
import { useIsMutating } from "@tanstack/react-query";
import { DrawerPageProps } from "./types";
import {
  UpdateBoxItemParams,
  useItem,
  useSelectedItemContext,
} from "../SelectedItemContext";
import { Cadence } from "../types";
import { ConsolidateShipmentPage } from "./pages/ConsolidateItemsPage";
import { SkipShipmentPage } from "./pages/SkipShipmentPage";

export type DrawerPageRenderer = (props: DrawerPageProps) => React.ReactNode;

export type Page =
  | { kind: "edit-subscription" }
  | { kind: "update-interval" }
  | { kind: "update-address" }
  | { kind: "edit-product" }
  | { kind: "consolidate-shipment" }
  | {
      kind: "swap-item";
      isSubscription: boolean;
      cadence: Cadence | null;
      initialFilter?: string;
    }
  | {
      kind: "confirm-swap-product";
      swapProduct: DroppsProduct;
      onChange: (item: UpdateBoxItemParams) => void;
    }
  | {
      kind: "swap-item-scent";
      scentFilter: string;
      isSubscription: boolean;
      cadence: Cadence | null;
    }
  | { kind: "skip-shipment" }
  | { kind: "delay-shipment" }
  | { kind: "apply-discount" }
  | { kind: "cancel-subscription" };

export type PortalDrawerPageProps = DrawerPageProps & {
  page: Page;
};

export const EditUpcomingOrderDrawer = ({
  page,
  ...rest
}: PortalDrawerPageProps) => {
  const itemUpdator = useItem();
  const { rechargeItem } = useSelectedItemContext().boxItem;
  const { notify, goToFirstPage } = rest;

  const handleSwapItem = (swapItem: UpdateBoxItemParams): void => {
    itemUpdator.mutate(swapItem, {
      onSuccess: () => {
        notify({
          title: "Product Swapped",
          message: `You are now subscribed to ${swapItem.product.title}`,
        });
        goToFirstPage();
      },
      onError: () => {
        notify({
          title: "Error",
          message: `An error occurred. Please try again.`,
          severity: "error",
        });
      },
    });
  };
  switch (page?.kind) {
    case "edit-subscription":
      return <EditSubscriptionPage {...page} {...rest} />;
    case "update-interval":
      return <UpdateIntervalPage {...page} {...rest} />;
    case "update-address":
      return <UpdateAddressPage {...page} {...rest} />;
    case "edit-product":
      return <EditProductPage {...page} {...rest} />;
    case "consolidate-shipment":
      return (
        <ConsolidateShipmentPage
          {...page}
          nextChargeDate={rechargeItem.nextChargeDate}
          box={undefined}
          {...rest}
        />
      );
    case "swap-item":
      return (
        <ProductSelectorPage
          {...rest}
          nextChargeDate={rechargeItem.nextChargeDate}
          displayMode={page.isSubscription ? "onlySubscription" : "onlySpot"}
          onChange={handleSwapItem}
          cadence={page.cadence ?? undefined}
          initialFilter={page.initialFilter ?? ""}
          drawerTitle="Swap Product"
        />
      );
    case "confirm-swap-product":
      return (
        <ConfirmItemPage
          product={page.swapProduct}
          onChange={page.onChange}
          nextChargeDate={rechargeItem.nextChargeDate}
          {...rest}
        />
      );
    case "swap-item-scent":
      return (
        <ProductSelectorPage
          {...rest}
          onChange={handleSwapItem}
          displayMode={page.isSubscription ? "onlySubscription" : "onlySpot"}
          initialFilter={page.scentFilter}
          cadence={page.cadence ?? undefined}
          nextChargeDate={rechargeItem.nextChargeDate}
        />
      );
    case "delay-shipment":
      return <DelayShipmentPage {...page} {...rest} />;
    case "skip-shipment":
      return <SkipShipmentPage {...page} {...rest} />;
    case "apply-discount":
      return null;
    case "cancel-subscription":
      return <CancelSubscriptionPage {...page} {...rest} />;
  }
};

export type PortalDrawerProps = DrawerPageProps & {
  contents?: DrawerPageRenderer;
  open?: boolean;
  transparentBackdrop?: boolean;
  title: string;
};

export const PortalDrawer = ({
  contents,
  open,
  onExit: origExit,
  onBack,
  notify,
  title,
  goToFirstPage: originalGoToFirstPage,
  transparentBackdrop = false,
}: PortalDrawerProps) => {
  const isLoading = !!useIsMutating();

  const fullScreen: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  const onExit = () => {
    onBack?.();
    origExit();
  };

  const goToFirstPage = () => {
    onBack?.();
    originalGoToFirstPage();
  };

  return (
    <FullScreenDrawer
      anchor="right"
      open={open}
      onClose={onExit}
      fullscreen={fullScreen ? "true" : undefined}
      role="dialog"
      aria-label={title}
      aria-modal={true}
      BackdropProps={transparentBackdrop ? { className: "bg-transparent" } : {}}
    >
      <DrawerHeader
        sticky={true}
        handleClose={onExit}
        handleBack={onBack}
        text={title}
      />
      <div className="w-full space-y-4 p-5">
        {contents?.({ notify, onExit, onBack, goToFirstPage }) ?? null}
      </div>
      {isLoading && (
        <>
          <div className="absolute h-full w-full cursor-wait bg-white opacity-50 transition"></div>
          <div className="absolute flex h-full w-full cursor-wait items-center justify-center">
            <CircularProgress />
          </div>
        </>
      )}
    </FullScreenDrawer>
  );
};
